/**
 * Case Notes Component
 * Complete case notes system with rich text editing and voice recording
 */

import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { FileText, Mic, Edit, Trash2, Check, X, Clock } from 'lucide-react';
import { RichTextEditor } from './RichTextEditor';
import { VoiceRecorder } from './VoiceRecorder';
import { config } from '@/src/config';
import { useFormattedDate } from '../hooks/useUserTimezone';
import { offlineAwareMutate } from '@/lib/offline/hooks';

interface CaseNote {
  id: number;
  referral_id: string;
  user_id: number;
  content: string;
  content_html?: string;
  audio_url?: string;
  language?: string;
  transcription?: string;
  created_at: string;
  updated_at?: string;
  is_voice_note: boolean;
  author_name?: string;
  author_email?: string;
}

interface CaseNotesProps {
  referralId: string;
  userId: number;
  apiBaseUrl?: string;
}

export function CaseNotes({ referralId, userId, apiBaseUrl = config.api.baseUrl }: CaseNotesProps) {
  const { fmt } = useFormattedDate();
  const [notes, setNotes] = useState<CaseNote[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');

  // New note state
  const [noteContent, setNoteContent] = useState('');
  const [noteContentHtml, setNoteContentHtml] = useState('');
  const [activeTab, setActiveTab] = useState<'text' | 'voice'>('text');

  // Edit state
  const [editingNoteId, setEditingNoteId] = useState<number | null>(null);
  const [editContent, setEditContent] = useState('');
  const [editContentHtml, setEditContentHtml] = useState('');

  // Load notes on mount
  useEffect(() => {
    fetchNotes();
  }, [referralId]);

  const fetchNotes = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${apiBaseUrl}/api/case-notes/?referral_id=${referralId}`);

      if (!response.ok) {
        throw new Error('Failed to fetch notes');
      }

      const data = await response.json();
      setNotes(data);
      setError('');
    } catch (err) {
      console.error('Error fetching notes:', err);
      setError('Failed to load case notes');
    } finally {
      setLoading(false);
    }
  };

  const handleAddNote = async (content: string, html: string, isVoiceNote = false, audioBlob?: Blob, language?: string) => {
    try {
      setLoading(true);
      console.log('📝 CaseNotes: handleAddNote called', {
        content: content.substring(0, 50) + '...',
        isVoiceNote,
        language,
        referralId,
        userId
      });

      const noteData: any = {
        referral_id: referralId,
        user_id: userId,
        content,
        content_html: html,
        is_voice_note: isVoiceNote,
        language: language || 'en'
      };

      // For voice notes with audio, we'd upload the audio blob separately
      // For now, we'll just save the transcription
      if (audioBlob) {
        // In a full implementation, upload audioBlob to a file storage service
        // and include the URL in noteData.audio_url
        console.log('🎵 Audio blob available:', audioBlob.size, 'bytes');
      }

      console.log('📤 Posting note to API:', noteData);
      const result: any = await offlineAwareMutate(
        'CREATE',
        'case-notes',
        undefined,
        noteData,
        async () => {
          const response = await fetch(`${apiBaseUrl}/api/case-notes/`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(noteData),
          });

          if (!response.ok) {
            const errorText = await response.text();
            console.error('❌ API Error:', response.status, errorText);
            throw new Error('Failed to create note');
          }
          return response.json();
        },
      );

      // Refresh notes list (online); offline the write is queued.
      if (result && result.queued) {
        alert('Saved offline — will sync when you reconnect.');
      } else {
        console.log('✅ Note saved successfully:', result);
        await fetchNotes();
        console.log('🔄 Notes list refreshed');
      }

      // Reset form
      setNoteContent('');
      setNoteContentHtml('');
      setError('');
    } catch (err) {
      console.error('❌ Error adding note:', err);
      setError('Failed to add note');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateNote = async (noteId: number) => {
    try {
      setLoading(true);

      const updateBody = {
        content: editContent,
        content_html: editContentHtml,
      };

      const result: any = await offlineAwareMutate(
        'UPDATE',
        'case-notes',
        String(noteId),
        updateBody,
        async () => {
          const response = await fetch(`${apiBaseUrl}/api/case-notes/${noteId}`, {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(updateBody),
          });

          if (!response.ok) {
            throw new Error('Failed to update note');
          }
          return response.json();
        },
      );

      // Refresh notes list (online); offline the write is queued — skip the
      // network refresh and just exit edit mode optimistically.
      if (result && result.queued) {
        alert('Saved offline — will sync when you reconnect.');
      } else {
        await fetchNotes();
      }

      // Exit edit mode
      setEditingNoteId(null);
      setEditContent('');
      setEditContentHtml('');
      setError('');
    } catch (err) {
      console.error('Error updating note:', err);
      setError('Failed to update note');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteNote = async (noteId: number) => {
    if (!confirm('Are you sure you want to delete this note?')) {
      return;
    }

    try {
      setLoading(true);

      const response = await fetch(`${apiBaseUrl}/api/case-notes/${noteId}`, {
        method: 'DELETE',
      });

      if (!response.ok) {
        throw new Error('Failed to delete note');
      }

      // Refresh notes list
      await fetchNotes();
      setError('');
    } catch (err) {
      console.error('Error deleting note:', err);
      setError('Failed to delete note');
    } finally {
      setLoading(false);
    }
  };

  const startEditing = (note: CaseNote) => {
    setEditingNoteId(note.id);
    setEditContent(note.content);
    setEditContentHtml(note.content_html || note.content);
  };

  const cancelEditing = () => {
    setEditingNoteId(null);
    setEditContent('');
    setEditContentHtml('');
  };

  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} min ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;

    return fmt.dateTime(timestamp);
  };

  return (
    <Card className="border border-blue-200/50 shadow-2xl hover:shadow-3xl transition-all duration-500 bg-white/95 backdrop-blur-sm rounded-3xl overflow-hidden group hover:scale-[1.01] transform">
      <CardHeader className="pb-6 bg-gradient-to-r from-blue-50/90 to-indigo-50/90 border-b border-blue-200/40 rounded-t-3xl">
        <CardTitle className="flex items-center space-x-3 text-xl font-semibold text-gray-800">
          <div className="p-3 bg-gradient-to-br from-blue-600 to-indigo-600 rounded-2xl shadow-xl group-hover:shadow-2xl transition-all duration-300 group-hover:scale-110 transform">
            <FileText className="w-6 h-6 text-white" />
          </div>
          <span>Case Notes</span>
          <span className="text-sm font-normal text-gray-600">({notes.length})</span>
        </CardTitle>
      </CardHeader>

      <CardContent className="p-6 space-y-6">
        {/* Error Message */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-3">
            <p className="text-sm text-red-800">{error}</p>
          </div>
        )}

        {/* Add New Note */}
        <div className="space-y-4">
          <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as 'text' | 'voice')}>
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="text" className="flex items-center gap-2">
                <FileText className="w-4 h-4" />
                Text Note
              </TabsTrigger>
              <TabsTrigger value="voice" className="flex items-center gap-2">
                <Mic className="w-4 h-4" />
                Voice Note
              </TabsTrigger>
            </TabsList>

            <TabsContent value="text" className="space-y-4 mt-4">
              <RichTextEditor
                value={noteContentHtml}
                onChange={(text, html) => {
                  setNoteContent(text);
                  setNoteContentHtml(html);
                }}
                placeholder="Enter case notes, updates, or observations..."
                minHeight="150px"
              />
              <Button
                onClick={() => handleAddNote(noteContent, noteContentHtml, false)}
                disabled={!noteContent.trim() || loading}
                className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 text-white hover:from-blue-700 hover:to-indigo-700 shadow-xl rounded-xl py-4 text-lg font-semibold"
              >
                <FileText className="w-4 h-4 mr-2" />
                Add Text Note
              </Button>
            </TabsContent>

            <TabsContent value="voice" className="space-y-4 mt-4">
              <VoiceRecorder
                onTranscription={(text, audioBlob, language) => {
                  console.log('🎙️ CaseNotes: onTranscription callback received', {
                    text: text.substring(0, 50) + (text.length > 50 ? '...' : ''),
                    textLength: text.length,
                    hasAudioBlob: !!audioBlob,
                    language
                  });
                  if (text.trim()) {
                    handleAddNote(text, text, true, audioBlob, language);
                  } else {
                    console.warn('⚠️ Transcription text is empty, not saving note');
                  }
                }}
              />
            </TabsContent>
          </Tabs>
        </div>

        {/* Notes List */}
        <div className="space-y-4 pt-6 border-t border-blue-200/50">
          <h3 className="font-semibold text-lg text-gray-800 flex items-center gap-2">
            <Clock className="w-5 h-5 text-blue-600" />
            Note History
          </h3>

          {loading && notes.length === 0 && (
            <div className="text-center py-8 text-gray-500">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
              <p className="mt-2">Loading notes...</p>
            </div>
          )}

          {!loading && notes.length === 0 && (
            <div className="text-center py-8 text-gray-500">
              <FileText className="w-12 h-12 mx-auto mb-2 text-gray-400" />
              <p>No notes yet. Add your first note above.</p>
            </div>
          )}

          {notes.map((note) => (
            <div
              key={note.id}
              className="p-6 bg-gradient-to-r from-blue-50/80 to-indigo-50/80 border border-blue-200/50 rounded-2xl space-y-3"
            >
              {/* Note Header */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-indigo-500 rounded-full flex items-center justify-center">
                    {note.is_voice_note ? (
                      <Mic className="w-4 h-4 text-white" />
                    ) : (
                      <FileText className="w-4 h-4 text-white" />
                    )}
                  </div>
                  <div>
                    <p className="font-semibold text-gray-800 flex items-center gap-2">
                      {note.author_name || 'Unknown User'}
                      {note.is_voice_note && (
                        <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full">
                          Voice {note.language && `(${note.language})`}
                        </span>
                      )}
                    </p>
                    <p className="text-xs text-gray-600">{formatTimestamp(note.created_at)}</p>
                    {note.updated_at && note.updated_at !== note.created_at && (
                      <p className="text-xs text-gray-500 italic">Edited {formatTimestamp(note.updated_at)}</p>
                    )}
                  </div>
                </div>

                {/* Actions */}
                {editingNoteId !== note.id && (
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => startEditing(note)}
                      className="border-blue-300 hover:bg-blue-50"
                    >
                      <Edit className="w-3 h-3" />
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleDeleteNote(note.id)}
                      className="border-red-300 hover:bg-red-50 text-red-600"
                    >
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                )}
              </div>

              {/* Note Content */}
              {editingNoteId === note.id ? (
                <div className="space-y-3">
                  <RichTextEditor
                    value={editContentHtml}
                    onChange={(text, html) => {
                      setEditContent(text);
                      setEditContentHtml(html);
                    }}
                    minHeight="120px"
                  />
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      onClick={() => handleUpdateNote(note.id)}
                      disabled={!editContent.trim() || loading}
                      className="bg-v2accent hover:bg-v2accent/90 text-white"
                    >
                      <Check className="w-3 h-3 mr-1" />
                      Save
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={cancelEditing}
                      className="border-gray-300"
                    >
                      <X className="w-3 h-3 mr-1" />
                      Cancel
                    </Button>
                  </div>
                </div>
              ) : (
                <div
                  className="text-gray-800 text-base leading-relaxed prose prose-sm max-w-none"
                  dangerouslySetInnerHTML={{
                    __html: note.content_html || note.content.replace(/\n/g, '<br/>')
                  }}
                />
              )}
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
